import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { useDonations } from '@/hooks/useDonations';
import { setDonationDelivered, type Donation } from '@/services/donations';
import DonationModal from './components/DonationModal';
import DonationEntriesModal from './components/DonationEntriesModal';
import DonationReport from './components/DonationReport';

export default function DonationsPage() {
  const { data, loading, error, refetch } = useDonations();
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<Donation | null>(null);
  const [entriesFor, setEntriesFor] = useState<Donation | null>(null);
  const [togglingId, setTogglingId] = useState('');

  const totalDelivered = data.filter((d) => d.isDelivered).length;

  const filtered = data.filter((d) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (d.periodName || d.beneficiaryName).toLowerCase().includes(q);
  });

  async function handleToggle(id: string, isDelivered: boolean) {
    setTogglingId(id);
    await setDonationDelivered(id, !isDelivered);
    setTogglingId('');
    refetch();
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && (
          <div className="flex items-center gap-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            <i className="ri-error-warning-line text-lg"></i>
            <span className="flex-1">{error}</span>
            <button
              onClick={refetch}
              className="px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Reintentar
            </button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Donaciones</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{data.length} fundaciones registradas</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-add-line mr-1.5"></i>Nueva Donación
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Fundaciones</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{data.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Entregadas</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">{totalDelivered}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Pendientes</p>
            <p className="text-xl font-bold text-amber-600 mt-1">{data.length - totalDelivered}</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar fundación..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Fundación</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">%</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-center">Estado</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-32 text-center">Acción</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-10 text-center text-foreground-400">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando donaciones...
                    </td>
                  </tr>
                ) : (
                  filtered.map((d) => (
                    <tr key={d.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                            <i className="ri-heart-line text-accent-600"></i>
                          </div>
                          <div>
                            <p className="text-foreground-900 font-medium">{d.periodName || d.beneficiaryName || '—'}</p>
                            <p className="text-xs text-foreground-500">
                              {[d.beneficiaryPhone, d.beneficiaryAddress].filter(Boolean).join(' · ') || 'Sin datos de contacto'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right text-foreground-600 whitespace-nowrap">{d.donationRate}%</td>
                      <td className="px-5 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${d.isDelivered ? 'text-emerald-700 bg-emerald-50' : 'text-amber-700 bg-amber-50'}`}>
                          {d.isDelivered ? 'Entregada' : 'Pendiente'}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setEntriesFor(d)}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                            title="Historial de valores donados"
                          >
                            <i className="ri-list-check-3 text-foreground-500 text-lg"></i>
                          </button>
                          <button
                            onClick={() => handleToggle(d.id, d.isDelivered)}
                            disabled={togglingId === d.id}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
                            title={d.isDelivered ? 'Marcar pendiente' : 'Marcar entregada'}
                          >
                            <i className={`${d.isDelivered ? 'ri-checkbox-circle-fill text-emerald-600' : 'ri-checkbox-blank-circle-line text-foreground-400'} text-lg`}></i>
                          </button>
                          <button
                            onClick={() => setEditing(d)}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                            title="Editar donación"
                          >
                            <i className="ri-file-edit-line text-foreground-500 text-lg"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-heart-line text-3xl block mb-2"></i>
              <p className="text-sm">
                {data.length === 0
                  ? 'Aún no hay donaciones registradas.'
                  : 'No se encontraron donaciones con ese filtro.'}
              </p>
            </div>
          )}
        </div>

        <DonationReport />
      </div>

      {showNew && <DonationModal onClose={() => setShowNew(false)} onSaved={refetch} />}
      {editing && <DonationModal donation={editing} onClose={() => setEditing(null)} onSaved={refetch} />}
      {entriesFor && (
        <DonationEntriesModal donation={entriesFor} onClose={() => setEntriesFor(null)} onSaved={refetch} />
      )}
    </AdminLayout>
  );
}